import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { SegmentedTabs, segmentedTabPanelProps } from "@/components/ui/segmented-tabs";
import { ReviewTabTransition } from "@/components/case-checking/ReviewTabTransition";
import { ReviewSummaryTab } from "@/components/case-checking/ReviewSummaryTab";
import { ReviewDeepdiveTab } from "@/components/case-checking/ReviewDeepdiveTab";
import { ReviewExportButton } from "@/components/case-checking/ReviewExportButton";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import { CaseCheckMissionAttentionBanner } from "@/components/case-checking/CaseCheckMissionAttentionBanner";
import { CC_ERROR_TEXT, CC_TAB_PANEL_PT } from "@/components/case-checking/caseCheckingChrome";
import type { CaseCheckingGrade } from "@/components/case-checking/CaseReviewOverviewPanel";
import {
  downloadRemedialsXlsx,
  downloadReviewDeepdiveXlsx,
  downloadReviewSummaryXlsx,
  type CaseAgenticMissionLink,
  type CaseReview as ApiCaseReview,
} from "@/services/api/case-checking";

type SummaryTab = "summary" | "deepdive" | "actions";

export function CaseSummaryView({
  caseId,
  caseReview,
  grade,
  displayAdviser,
  fallbackAdviser,
  fallbackGrade,
  openRemedialCount,
  severeRemedialCount,
  remedialsContent,
  showReadOnlyBanner = true,
  companyId,
  sectionLabel,
  isAgenticProcessing = false,
  showMissionAttentionBanner = false,
  agenticProgressLabel = null,
  missionLink = null,
  onRetryChain,
  onRetryTask,
  onSkipAdjudication,
  onAddDocuments,
  onOpenMission,
}: {
  caseId: string;
  caseReview?: ApiCaseReview | null;
  grade: CaseCheckingGrade;
  displayAdviser?: string;
  fallbackAdviser?: string;
  fallbackGrade: CaseCheckingGrade;
  openRemedialCount: number;
  severeRemedialCount: number;
  remedialsContent: ReactNode;
  showReadOnlyBanner?: boolean;
  companyId?: string;
  sectionLabel?: string;
  isAgenticProcessing?: boolean;
  showMissionAttentionBanner?: boolean;
  agenticProgressLabel?: string | null;
  missionLink?: CaseAgenticMissionLink | null;
  onRetryChain?: () => void;
  onRetryTask?: () => void;
  onSkipAdjudication?: () => void;
  onAddDocuments?: () => void;
  onOpenMission?: () => void;
}) {
  const [tab, setTab] = useState<SummaryTab>("summary");
  const [focusStatementId, setFocusStatementId] = useState<string | null>(null);
  const [focusRuleId, setFocusRuleId] = useState<string | null>(null);
  const [deepdiveEnterKey, setDeepdiveEnterKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { scorable } = useProblemStatementRun(caseId);

  const tabs = [
    { id: "summary" as const, label: "Summary" },
    { id: "deepdive" as const, label: "Deepdive" },
    {
      id: "actions" as const,
      label: "Actions",
      count: openRemedialCount,
      countLabel:
        severeRemedialCount > 0
          ? `${severeRemedialCount} high`
          : openRemedialCount > 0
            ? `${openRemedialCount} open`
            : undefined,
      countVariant: severeRemedialCount > 0 ? ("attention" as const) : ("neutral" as const),
    },
  ];

  const resetWindowContentScroll = () => {
    const pane = document.querySelector('[data-testid="window-content-scroll"]') as HTMLElement | null;
    if (pane) pane.scrollTop = 0;
  };

  const openDeepdive = (statementId: string, ruleId: string | null) => {
    setFocusStatementId(statementId);
    setFocusRuleId(ruleId);
    setDeepdiveEnterKey((k) => k + 1);
    setTab("deepdive");
    resetWindowContentScroll();
  };

  const handleTabExport = () => {
    setExporting(true);
    setExportError(null);
    const download =
      tab === "summary"
        ? downloadReviewSummaryXlsx(caseId, `review_summary_${caseId.slice(-6)}.xlsx`)
        : tab === "deepdive"
          ? downloadReviewDeepdiveXlsx(caseId, `review_deepdive_${caseId.slice(-6)}.xlsx`)
          : downloadRemedialsXlsx(caseId, `remedials_${caseId.slice(-6)}.xlsx`);
    void download
      .catch((e) => setExportError(e instanceof Error ? e.message : "Export failed"))
      .finally(() => setExporting(false));
  };

  const exportScopeLabel =
    tab === "summary" ? "summary" : tab === "deepdive" ? "deep dive" : "remedials";
  const exportTestId =
    tab === "summary"
      ? "case-summary-summary-export"
      : tab === "deepdive"
        ? "case-summary-deepdive-export"
        : "case-summary-actions-export";
  const exportDisabled =
    (tab === "summary" && scorable.length === 0) || (tab === "actions" && openRemedialCount === 0);

  const showContextBanner = showMissionAttentionBanner || showReadOnlyBanner;

  return (
    <div data-testid="case-summary-view">
      {sectionLabel ? (
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">{sectionLabel}</p>
      ) : null}

      {showContextBanner ? (
        <div className="mb-4">
          {showMissionAttentionBanner ? (
            <CaseCheckMissionAttentionBanner
              missionLink={missionLink}
              companyId={companyId}
              progressLabel={agenticProgressLabel}
              onRetryChain={onRetryChain}
              onRetryTask={onRetryTask}
              onSkipAdjudication={onSkipAdjudication}
              onAddDocuments={onAddDocuments}
              onOpenMission={onOpenMission}
            />
          ) : (
            <p className="text-sm leading-relaxed text-slate-500">
              Read-only view of the review outcome. Switch to{" "}
              <span className="text-slate-700 dark:text-slate-200">Case Review</span> to make changes.
            </p>
          )}
        </div>
      ) : null}

      <div className="flex min-w-0 items-center gap-3 border-b border-slate-200 dark:border-slate-800/80">
        <SegmentedTabs
          className="min-w-0 flex-1 overflow-x-auto scrollbar-hide"
          showDivider={false}
          ariaLabel="Case summary sections"
          panelIdPrefix="case-summary-tab-panel"
          tabTestIdPrefix="case-summary-tab"
          activeId={tab}
          onChange={(id) => {
            setTab(id as SummaryTab);
            setExportError(null);
            if (id === "deepdive") resetWindowContentScroll();
          }}
          tabs={tabs}
        />
        <div className="flex shrink-0 items-center justify-center self-center">
          <ReviewExportButton
            testId={exportTestId}
            scopeLabel={exportScopeLabel}
            loading={exporting}
            disabled={exportDisabled}
            onClick={handleTabExport}
          />
        </div>
      </div>

      {exportError ? <p className={clsx("mt-2 text-xs", CC_ERROR_TEXT)}>{exportError}</p> : null}

      <ReviewTabTransition
        tabKey={tab}
        className={CC_TAB_PANEL_PT}
        {...segmentedTabPanelProps(tab, tab, {
          panelIdPrefix: "case-summary-tab-panel",
          tabIdPrefix: "case-summary-tab",
        })}
      >
        {tab === "summary" ? (
          <ReviewSummaryTab
            caseId={caseId}
            caseReview={caseReview}
            grade={grade}
            displayAdviser={displayAdviser}
            fallbackAdviser={fallbackAdviser}
            onSelectStatement={(id) => openDeepdive(id, null)}
            onSelectRuleIssue={(statementId, ruleId) => openDeepdive(statementId, ruleId)}
            companyId={companyId}
            isAgenticProcessing={isAgenticProcessing}
          />
        ) : null}
        {tab === "deepdive" ? (
          <ReviewDeepdiveTab
            caseId={caseId}
            focusStatementId={focusStatementId}
            focusRuleId={focusRuleId}
            enterKey={deepdiveEnterKey}
            caseReview={caseReview}
            fallbackGrade={fallbackGrade}
            readOnly
            companyId={companyId}
          />
        ) : null}
        {tab === "actions" ? (
          <div data-testid="case-summary-actions-tab">{remedialsContent}</div>
        ) : null}
      </ReviewTabTransition>
    </div>
  );
}
